/**
 * V111: MM 관리 (Man-Month) FE 타입 정의
 *
 * @module shared/types/mm
 */

import { GradeBasedFTE, StaffAssignment } from './project';

// ============================================
// getMmOverviewData API
// ============================================

/**
 * getMmOverviewData 요청
 */
export interface GetMmOverviewDataRequest {
  /** 대상 월 (YYYY-MM 형식) */
  month: string;

  /** V233.1: 회사 코드 (optional — 미전달 시 BE가 확정) */
  companyCode?: 'KR' | 'CN';

  /**
   * V131.3: 성능 디버그 정보 요청
   *
   * true로 설정하면 응답에 BE 성능 계측 데이터가 포함됩니다.
   * - 권한: adminLike 권한 필요 (없으면 무시됨)
   * - 캐시: debugPerf=true일 때 서버 캐시 bypass (정확한 측정용)
   */
  debugPerf?: boolean;

  /**
   * V132.13: BE 서버 캐시 우회
   *
   * true로 설정하면 BE 서버 메모리 캐시를 무시하고 Firestore에서 직접 조회합니다.
   * - 용도: 저장 후 refetch 시 최신 데이터 보장
   * - 주의: Cloud Function 컨테이너가 격리되어 있어 saveMmProjectNote에서
   *         캐시를 무효화해도 getMmOverviewData 컨테이너의 캐시는 영향 없음
   */
  skipServerCache?: boolean;

  /**
   * V73.3: 호출 페이지 구분 (MM현황 vs MM작성)
   *
   * - 'overview': MM현황 → KR role_manager 스코프 ALL 승격 적용
   * - 'register': MM작성 → KR role_manager 스코프 SUBTREE 유지
   */
  viewMode?: 'overview' | 'register';
}

/**
 * V131.3: 성능 디버그 정보
 *
 * BE에서 측정된 단계별 성능 데이터입니다.
 */
export interface DebugPerfInfo {
  /** 함수 실행 리전 (us-central1 | asia-northeast3) */
  region: string;

  /** 단계별 절대 시간 (ms, 시작 시점 기준) */
  stepsAbs: Record<string, number>;

  /** 단계별 구간 시간 (ms, 이전 단계 기준) */
  stepsDelta: Record<string, number>;

  /** 총 실행 시간 (ms) */
  totalMs: number;

  /** 캐시 히트 여부 */
  cacheHit?: boolean;

  /** 조회된 팀 수 */
  teamsCount?: number;

  /** 후보 리비전 문서 수 */
  candidateDocsCount?: number;
}

/**
 * getMmOverviewData 응답
 */
export interface GetMmOverviewDataResponse {
  success: boolean;

  /** 팀별 MM 요약 */
  teams: MmTeamSummary[];

  /** 해당월 정보 */
  monthInfo: {
    /** 근무일수 */
    workingDays: number;
    /** 공휴일 목록 */
    holidays: Array<{ date: string; name: string }>;
  };

  error?: string;

  /** V233.1: BE가 확정한 companyCode */
  resolvedCompanyCode: 'KR' | 'CN';

  /**
   * V131.3: 성능 디버그 정보
   *
   * debugPerf=true 요청 + adminLike 권한 시에만 포함됩니다.
   */
  debugPerf?: DebugPerfInfo;

  /**
   * V233.1: 팀 0건 원인 진단 정보
   *
   * debugPerf=true + teams=0일 때만 포함.
   */
  debugInfo?: {
    companyCode: string;
    scopeMode: string;
    totalOrgTeams: number;
    scopeTeamCount: number;
    mmEnabledCount: number;
    userRoles: string[];
    userOrgUnitId: string | null;
  };
}

// ============================================
// V114.3: 직원 정보 타입 (패널 표시용)
// ============================================

/**
 * V114.3: MM 직원 정보 (패널 표시용)
 *
 * 투입/유휴/중복투입 패널에서 표시되는 직원 정보
 */
export interface MmEmployeeInfo {
  /** 직원 ID (userId) */
  userId: string;

  /** 이름 (displayName ?? name ?? employeeName) */
  name: string;

  /** 소속 조직명 */
  orgUnitName: string;

  /** 직책 (팀장, 실장 등) - jobTitle 기반 */
  jobTitle: string;

  /** 직위 (사원, 대리, 과장 등) - positionTitle 기반 */
  positionTitle: string;

  /**
   * V116.7.4: 선택월 기준 투입 프로젝트 수 (전사 기준)
   *
   * 계산 기준: 선택월에 유효한 리비전의 staffAssignments 기준
   * (프로젝트 status 무관, 종료된 프로젝트도 포함)
   */
  slotCount?: number;

  /**
   * V117.2: 해당 팀 프로젝트에 투입된 프로젝트 수 (팀 프로젝트 기준)
   *
   * - 2 이상이면 중복투입 (배지 표시: "중복투입 xN")
   * - 타팀 프로젝트 투입은 미포함
   *
   * 배지 표시 조건: isTeamMember === true && teamProjectSlotCount >= 2
   */
  teamProjectSlotCount?: number;

  /**
   * V117.2: 해당 팀 소속 여부
   *
   * true: 이 팀의 teamMemberIds에 포함된 직원
   * false: 타팀지원 또는 대련지사 직원
   */
  isTeamMember?: boolean;

  /**
   * V69 Phase 2: 외부/기타 인력 여부
   *
   * - true: 외부 인력. userId 는 BE 의 synthetic dedup key (예: __external__{org}__{name}).
   *   FE 는 externalName / externalOrgName 으로 "[기타] {externalOrgName} / {externalName}" 라벨 표시.
   *   클릭 시 직원 상세 사이드패널 미오픈 (users 컬렉션 미보유).
   * - false / undefined: 내부 직원
   */
  isExternal?: boolean;

  /** V69 Phase 2: 외부 인력 이름 (isExternal=true 시 채워짐) */
  externalName?: string;

  /** V69 Phase 2: 외부 인력 소속명 (isExternal=true 시 채워짐) */
  externalOrgName?: string;
}

/**
 * V114.3: 중복투입 직원 정보 (프로젝트명 포함)
 */
export interface MmDuplicatedEmployeeInfo extends MmEmployeeInfo {
  /** 투입 중인 프로젝트명 목록 */
  projectNames: string[];
}

/**
 * 팀 MM 요약
 *
 * V114: 인력현황 정의 전면 교체
 * - 투입인력: 프로젝트 투입 슬롯 수 (사람이 아닌 프로젝트별 투입 카운트)
 * - 유휴인력: 팀 소속 중 미투입 인원 (조직장 제외)
 * - 중복투입: 팀 소속 중 2개+ 프로젝트 투입 인원
 */
export interface MmTeamSummary {
  /** 팀 orgUnitId */
  teamId: string;

  /** 팀명 */
  teamName: string;

  // ============================================
  // V114: 인력현황 (새 정의)
  // ============================================

  /**
   * V114: 투입인력 (슬롯 합계)
   *
   * 해당 팀의 프로젝트에 해당월 1일 이상 투입된 슬롯 수
   * - 한 사람이 2개 프로젝트 투입 → 2로 카운트
   * - 의미: 프로젝트별 투입 슬롯의 합계
   */
  totalAssignedSlots: number;

  /**
   * V114: 팀인원 슬롯
   *
   * 투입인력 중 해당 팀(teamId) 소속 직원의 슬롯 수
   */
  assignedTeamSlots: number;

  /**
   * V114: 타팀지원 슬롯
   *
   * 투입인력 중 한국지사(KR)이면서 다른 팀 소속 직원의 슬롯 수
   * - 0이면 UI에서 미표시
   */
  assignedSupportSlots: number;

  /**
   * V114: 대련지사 슬롯
   *
   * 투입인력 중 CN(대련) 소속 직원의 슬롯 수
   * - 0이면 UI에서 미표시
   */
  assignedDalianSlots: number;

  /**
   * V69 Phase 2: 기타(외부) 슬롯
   *
   * 투입인력 중 외부 인력(isExternal=true) 슬롯 수
   * - totalAssignedSlots 에 합산 (team + support + dalian + other = total)
   * - 0이면 UI에서 미표시
   */
  assignedOtherSlots: number;

  /**
   * V114: 유휴인력 (사람 수)
   *
   * 팀 소속 인원 중 해당월 프로젝트 미투입 인원 수
   * - 조직장(leaderId/orgManagerEmployeeId)은 제외
   */
  idleMembers: number;

  /**
   * V114: 중복투입 (사람 수)
   *
   * 팀 소속 인원 중 2개 이상 프로젝트에 투입된 인원 수
   * - 조직장 포함 (유휴에서만 제외)
   */
  duplicatedMembers: number;

  /**
   * @deprecated V114.3에서 assignedEmployees/idleEmployees/duplicatedEmployees로 대체
   * V114.1: 중복투입 인원 이름 목록
   */
  duplicatedMemberNames: string[];

  // ============================================
  // V114.3: 직원 상세 리스트 (패널 표시용)
  // ============================================

  /**
   * V114.3: 투입 직원 리스트 (유니크)
   *
   * 해당 팀 프로젝트에 투입된 모든 직원 (팀원 + 타팀지원 + 대련)
   * 패널에서 클릭 시 상세 목록 표시
   */
  assignedEmployees: MmEmployeeInfo[];

  /**
   * V114.3: 유휴 직원 리스트
   *
   * 팀 소속 중 해당월 어떤 프로젝트에도 속하지 않은 직원
   * 조직장은 제외
   */
  idleEmployees: MmEmployeeInfo[];

  /**
   * V114.3: 중복투입 직원 리스트
   *
   * 팀 소속 중 2개+ 프로젝트에 투입된 직원 + 프로젝트명 목록
   */
  duplicatedEmployees: MmDuplicatedEmployeeInfo[];

  // ============================================
  // 기타 정보
  // ============================================

  /**
   * 리비전 변경 요약 (비고)
   *
   * 해당월에 발생한 계약/리비전 변경 중 "인력 구성 변경"만 텍스트 요약
   * 예: "1/16 중급 1명 추가투입", "1/20 초급 1명 제외"
   */
  revisionChangeSummary: string;

  /**
   * 팀 총 야근시간 (분)
   *
   * 팀원 전체의 해당월 승인 야근 합계
   */
  teamOvertimeMinutes: number;

  /**
   * 팀 총 인원 수
   *
   * users 컬렉션 기준 팀원 전체 수
   */
  teamMemberCount: number;

  /** 팀원 목록 (userId) */
  teamMemberIds: string[];

  /**
   * V114: 팀 조직장 ID
   *
   * 유휴인력 계산에서 제외할 조직장
   * orgUnits.leaderId 또는 orgManagerEmployeeId
   */
  teamLeaderId?: string | null;

  /** 프로젝트별 데이터 */
  projects: MmProjectData[];
}

// ============================================
// V114.4: 등급별 인원 카운트 타입
// ============================================

/**
 * V114.4: 계약 등급별 인원 카운트
 *
 * 계약서 기준 등급별 인원 수 (특/고/중/초/무)
 * - contractGradeCounts 합계 = contractStaffCount
 */
export interface ContractGradeCounts {
  /** 특급 */
  special: number;
  /** 고급 */
  high: number;
  /** 중급 */
  mid: number;
  /** 초급 */
  low: number;
  /** 등급무관 */
  none: number;
}

/**
 * V114.4: 실투입 등급별 인원 카운트
 *
 * 실제 배정된 인원의 등급별 수 (특/고/중/초)
 * - 등급무관은 집계 제외 (unknown 처리)
 * - actualGradeCounts 합계 ≤ actualStaffCount (등급무관 제외 시)
 */
export interface ActualGradeCounts {
  /** 특급 */
  special: number;
  /** 고급 */
  high: number;
  /** 중급 */
  mid: number;
  /** 초급 */
  low: number;
}

// ============================================
// V128: 등급별 추가근무 타입
// ============================================

/**
 * V128: 등급별 추가근무 (분)
 *
 * 프로젝트에 배정된 직원의 등급별 OT 시간 합계
 * - 등급은 해당 프로젝트의 staffAssignments[].grade 기준
 * - OT 요청자(requesterId)의 프로젝트 내 등급으로 집계
 */
export interface OvertimeByGrade {
  /** 특급 OT (분) */
  special: number;
  /** 고급 OT (분) */
  high: number;
  /** 중급 OT (분) */
  mid: number;
  /** 초급 OT (분) */
  low: number;
  /** 등급무관 OT (분) */
  none: number;
}

/**
 * 프로젝트 MM 데이터
 */
export interface MmProjectData {
  /** 프로젝트 ID */
  projectId: string;

  /** 프로젝트명 */
  projectName: string;

  // ============================================
  // 기본 정보
  // ============================================

  /**
   * 계약인력
   *
   * 등급 무관 수량 합계
   */
  contractStaffCount: number;

  /**
   * V114.4: 계약 등급별 인원 카운트
   *
   * 계약서 기준 등급별 인원 수
   * 합계 = contractStaffCount
   */
  contractGradeCounts: ContractGradeCounts;

  /**
   * V114.4 슬롯 패치: 실투입인력 (슬롯 합계)
   *
   * 월 유효 리비전의 staffAssignments 슬롯 합계
   * = 특 + 고 + 중 + 초 + 등급무관
   * (사람×프로젝트 중복 허용)
   */
  actualStaffCount: number;

  /**
   * V114.4 슬롯 패치: 실투입 등급별 인원 카운트
   *
   * 실제 배정된 슬롯의 등급별 수 (등급무관 제외)
   * 합계 = actualStaffCount - actualNoneCount
   */
  actualGradeCounts: ActualGradeCounts;

  /**
   * V114.4 슬롯 패치: 등급무관 슬롯 수 (디버그/검증용)
   *
   * UI에서는 표시하지 않지만, 검증용으로 별도 집계
   * actualStaffCount = (특+고+중+초) + actualNoneCount
   */
  actualNoneCount: number;

  // ============================================
  // MD 계산
  // ============================================

  /**
   * 기준MD
   *
   * (해당월 WD × 계약인력)
   * 리비전 구간별 계산 반영
   */
  baseMd: number;

  /**
   * 실투입MD
   *
   * V115: actualMd = (실투입인력 × WD 합계)
   * 야근MD는 프로젝트별 귀속 불가로 미포함
   * (baseMd와 독립적으로 계산)
   */
  actualMd: number;

  /**
   * 추가근무 (분)
   *
   * V121: 프로젝트별 OT 집계 (projectId 기준)
   * 팀 레벨도 집계 (MmTeamSummary.teamOvertimeMinutes 참조)
   */
  overtimeMinutes: number;

  /**
   * V128: 등급별 추가근무 (분)
   *
   * 프로젝트 배정자의 등급별 OT 시간
   */
  overtimeByGrade: OvertimeByGrade;

  // ============================================
  // 리비전 정보
  // ============================================

  /**
   * 리비전 구간 목록
   */
  revisions: MmRevisionSegment[];

  // ============================================
  // 메모 (mmProjectNotes)
  // ============================================

  notes: {
    /** 업무 상세내역 */
    workDetails: string;
    /** 특이사항 */
    remarks: string;
    /** 비용발생 */
    costOccurred: string;
  };

  // ============================================
  // 현재 리비전 상세 (표시용)
  // ============================================

  /** 현재 리비전 계약 인력 FTE */
  currentContractStaffing?: GradeBasedFTE;

  /** 현재 리비전 투입 인원 배정 */
  currentStaffAssignments?: StaffAssignment[];
}

/**
 * 리비전 구간
 *
 * V116: segment별 등급 카운트 + 변경 플래그 추가
 */
export interface MmRevisionSegment {
  /** 리비전 ID */
  revisionId: string;

  /** 리비전 번호 */
  revisionNo: number;

  /** 구간 시작일 (YYYY-MM-DD) */
  effectiveFrom: string;

  /** 구간 종료일 (YYYY-MM-DD) */
  effectiveTo: string;

  /** 구간 내 근무일수 */
  workingDays: number;

  /** 계약 인력 */
  contractStaffCount: number;

  /** 실투입 인력 */
  actualStaffCount: number;

  /** 구간 기준MD (계약인력 × WD) */
  baseMd: number;

  /**
   * V115: 구간 실투입MD (실투입인력 × WD)
   *
   * baseMd와 달리 실제 배정된 인력 기준으로 계산
   */
  actualMd: number;

  // ============================================
  // V116: segment별 등급 카운트
  // ============================================

  /** V116: 구간별 계약 등급 카운트 */
  contractGradeCounts: ContractGradeCounts;

  /** V116: 구간별 실투입 등급 카운트 */
  actualGradeCounts: ActualGradeCounts;

  // ============================================
  // V116: 변경 플래그 (이전 segment 대비)
  // ============================================

  /** V116: 계약인력 변경 여부 */
  contractChanged?: boolean;

  /** V116: 실투입인력 변경 여부 */
  actualChanged?: boolean;

  /** V116: 등급 변경 여부 */
  gradeChanged?: boolean;
}

// ============================================
// saveMmProjectNote API
// ============================================

/**
 * saveMmProjectNote 요청
 * V130: 개별 필드 patch 저장 지원 (선택적 필드)
 */
export interface SaveMmProjectNoteRequest {
  /** 팀 orgUnitId */
  teamId: string;

  /** 프로젝트 ID */
  projectId: string;

  /** 대상 월 (YYYY-MM 형식) */
  month: string;

  /** 업무 상세내역 (V130: optional) */
  workDetails?: string;

  /** 특이사항 (V130: optional) */
  remarks?: string;

  /** 비용발생 (V130: optional) */
  costOccurred?: string;
}

/**
 * saveMmProjectNote 응답
 */
export interface SaveMmProjectNoteResponse {
  success: boolean;

  /** 저장된 문서 ID */
  noteId?: string;

  error?: string;
}

// ============================================
// 유틸리티
// ============================================

/**
 * 추가근무(분)를 시:분 형식으로 변환
 */
export function formatOvertimeMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

/**
 * YYYY-MM 형식의 월 문자열 파싱
 */
export function parseMonth(monthStr: string): { year: number; month: number } {
  const [year, month] = monthStr.split('-').map(Number);
  return { year, month };
}

/**
 * 현재 월 (YYYY-MM)
 */
export function getCurrentMonth(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * 월 이동 (+/- months)
 */
export function addMonths(monthStr: string, delta: number): string {
  const { year, month } = parseMonth(monthStr);
  const date = new Date(year, month - 1 + delta, 1);
  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  return `${newYear}-${newMonth}`;
}

/**
 * 월 표시 포맷 (한국어)
 */
export function formatMonthKo(monthStr: string): string {
  const { year, month } = parseMonth(monthStr);
  return `${year}년 ${month}월`;
}

// ============================================
// V113: MM 조직 설정 API
// V113.1: 토글 의미 재정의 - MM현황 표시 여부만 제어
// ============================================

/**
 * setMmOrgEnabled 요청
 *
 * V113.1: 모든 orgUnit(회사/본부/실/팀) 설정 가능
 */
export interface SetMmOrgEnabledRequest {
  /** 조직 ID */
  orgUnitId: string;

  /** MM 표시 여부 */
  enabled: boolean;
}

/**
 * setMmOrgEnabled 응답
 */
export interface SetMmOrgEnabledResponse {
  success: boolean;
  error?: string;
}

/**
 * getMmOrgSettings 요청
 */
export interface GetMmOrgSettingsRequest {
  /** 회사 코드 (KR | CN) */
  companyCode: 'KR' | 'CN';
}

/**
 * getMmOrgSettings 응답
 */
export interface GetMmOrgSettingsResponse {
  success: boolean;

  /** 조직 목록 (회사/본부/실/팀 전체, enabled 상태 포함) */
  orgUnits: MmOrgSettingsItem[];

  error?: string;
}

/**
 * MM 조직 설정 아이템 (FE 표시용)
 *
 * V113.1: 모든 조직 타입(회사/본부/실/팀)에 토글 가능
 */
export interface MmOrgSettingsItem {
  /** 조직 ID */
  orgUnitId: string;

  /** 조직명 */
  name: string;

  /** 조직 유형 (company, headquarters, division, team) */
  type: string;

  /** 상위 조직 ID */
  parentId: string | null;

  /** MM 표시 여부 (V113.1: MM현황 페이지에 해당 조직 데이터 표시 여부) */
  enabled: boolean;

  /** 팀 여부 (V113.1: 참고용, 실제로는 모든 조직 토글 가능) */
  isTeam: boolean;
}
